import { useEffect, useState } from 'react';
import api from '@/lib/api';

interface StockItem {
  id: string;
  quantity: number;
  reserved: number;
  blocked: number;
  product: { sku: string; name: string; unit: string };
  warehouse: { name: string; code: string };
  location: { code: string } | null;
}

export default function StockPage() {
  const [stocks, setStocks] = useState<StockItem[]>([]);
  const [loadingData, setLoadingData] = useState(true);
  const [showAdjustModal, setShowAdjustModal] = useState(false);
  const [adjustForm, setAdjustForm] = useState({
    productId: '',
    warehouseId: '',
    type: 'ENTRADA',
    quantity: 0,
    reason: '',
  });

  useEffect(() => {
    api.get('/stock').then((r) => {
      setStocks(r.data);
      setLoadingData(false);
    }).catch(() => setLoadingData(false));
  }, []);

  const handleAdjust = async (e: React.FormEvent) => {
    e.preventDefault();
    await api.post('/stock/adjustments', adjustForm);
    setShowAdjustModal(false);
    setAdjustForm({ productId: '', warehouseId: '', type: 'ENTRADA', quantity: 0, reason: '' });
    api.get('/stock').then((r) => setStocks(r.data));
  };

  return (
    <>
    <div className="flex items-center justify-end mb-6">
        <button
          onClick={() => setShowAdjustModal(true)}
          className="px-4 py-2 bg-primary-600 text-white rounded-lg text-sm font-medium hover:bg-primary-700 transition-colors"
        >
          Ajustar Estoque
        </button>
      </div>

      {loadingData ? (
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
        </div>
      ) : (
        <div className="bg-white rounded-xl shadow-sm border border-dark-100 overflow-hidden">
          <table className="w-full">
            <thead className="bg-dark-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-dark-500 uppercase">SKU</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-dark-500 uppercase">Produto</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-dark-500 uppercase">Depósito</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-dark-500 uppercase">Endereço</th>
                <th className="px-6 py-3 text-right text-xs font-medium text-dark-500 uppercase">Quantidade</th>
                <th className="px-6 py-3 text-right text-xs font-medium text-dark-500 uppercase">Reservado</th>
                <th className="px-6 py-3 text-right text-xs font-medium text-dark-500 uppercase">Disponível</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-dark-100">
              {stocks.map((stock, index) => (
                <tr key={stock.id} className={index % 2 === 0 ? 'bg-white' : 'bg-dark-50/50'}>
                  <td className="px-6 py-4 text-sm font-medium text-primary-600">{stock.product.sku}</td>
                  <td className="px-6 py-4 text-sm text-dark-900">{stock.product.name}</td>
                  <td className="px-6 py-4 text-sm text-dark-600">{stock.warehouse.name}</td>
                  <td className="px-6 py-4 text-sm text-dark-600">{stock.location?.code || '-'}</td>
                  <td className="px-6 py-4 text-sm text-right font-medium text-dark-900">{stock.quantity}</td>
                  <td className="px-6 py-4 text-sm text-right text-yellow-600">{stock.reserved}</td>
                  <td className="px-6 py-4 text-sm text-right font-medium text-green-600">
                    {stock.quantity - stock.reserved}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {stocks.length === 0 && (
            <div className="text-center py-12 text-dark-500">Nenhum estoque encontrado</div>
          )}
        </div>
      )}

      {showAdjustModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl p-6 w-full max-w-md shadow-xl">
            <h2 className="text-lg font-bold text-dark-900 mb-4">Ajustar Estoque</h2>
            <form onSubmit={handleAdjust} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-dark-700 mb-1">Tipo</label>
                <select
                  value={adjustForm.type}
                  onChange={(e) => setAdjustForm({ ...adjustForm, type: e.target.value })}
                  className="w-full px-3 py-2 border border-dark-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
                >
                  <option value="ENTRADA">Entrada</option>
                  <option value="SAIDA">Saída</option>
                  <option value="AJUSTE">Ajuste</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-dark-700 mb-1">Produto ID</label>
                <input
                  type="text"
                  value={adjustForm.productId}
                  onChange={(e) => setAdjustForm({ ...adjustForm, productId: e.target.value })}
                  className="w-full px-3 py-2 border border-dark-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-dark-700 mb-1">Depósito ID</label>
                <input
                  type="text"
                  value={adjustForm.warehouseId}
                  onChange={(e) => setAdjustForm({ ...adjustForm, warehouseId: e.target.value })}
                  className="w-full px-3 py-2 border border-dark-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-dark-700 mb-1">Quantidade</label>
                <input
                  type="number"
                  value={adjustForm.quantity}
                  onChange={(e) => setAdjustForm({ ...adjustForm, quantity: parseInt(e.target.value) })}
                  className="w-full px-3 py-2 border border-dark-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-dark-700 mb-1">Motivo</label>
                <input
                  type="text"
                  value={adjustForm.reason}
                  onChange={(e) => setAdjustForm({ ...adjustForm, reason: e.target.value })}
                  className="w-full px-3 py-2 border border-dark-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
                  required
                />
              </div>
              <div className="flex gap-4 pt-4">
                <button
                  type="button"
                  onClick={() => setShowAdjustModal(false)}
                  className="flex-1 px-4 py-2 border border-dark-200 rounded-lg text-sm font-medium hover:bg-dark-50 transition-colors"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  className="flex-1 px-4 py-2 bg-primary-600 text-white rounded-lg text-sm font-medium hover:bg-primary-700 transition-colors"
                >
                  Confirmar
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </>
  );
}
